import React, { useState, useRef, useEffect } from 'react'
import {
  Grid, LinearProgress, MenuItem, DialogTitle,
  DialogContent, Button, Dialog, FormControl, Select, Box, Typography, Stack, Autocomplete, createFilterOptions, TextField, Input,
  Tooltip,
  IconButton,
  CircularProgress,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import stylesheet from '../detail-container.stylesheet'
import { ClassNameMap, withStyles } from '@mui/styles'
import Draggable from "react-draggable"
import DialogTitleHeaderComponent from '../common/dialog-title-header.component'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../redux/store'
import { PatientService } from '../../../../services/patient.service'
import Helper from '../../../../utils/helper'
import { toast } from 'react-toastify'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'
import { useUpdateDataAction } from '../../../../utils/update-details-hook'
import LibraryAddIcon from '@mui/icons-material/LibraryAdd'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import SiteListComponent from '../info-header/site-list.component'
import { formatDisplayName } from '../../../../utils/display-name-formatter'
import { isControlVisible } from '../comp-config'

interface IProps {
  open: boolean,
  input: any,
  handleDialogClose: any,
  classes: ClassNameMap
}

const AddTestDialogComponent: React.FC<IProps> = ({ open, input, handleDialogClose, classes }) => {
  const monitorRef = useRef(null) as any
  const [isLoading, setIsLoading] = useState(false)
  const [testsList, setTestsList] = useState([]) as any
  const [selectedSite, setSelectedSite] = useState(null) as any
  const [isAddSite, setIsAddSite] = useState(false)
  const [syncingSite, setSyncingSite] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null) as any

  // Test data state
  const [resultData, setResultData] = useState({
    test_name: '',
    status: Helper.isAIDA() ? 'ordered' : 'order_preparation'
  }) as any
  const [note, setNote] = useState('') as any
  const [isNoteFocused, setIsNoteFocused] = useState(false)
  const [dateRecorded, setDateRecorded] = useState(moment().format('YYYY-MM-DD')) as any
  const [scheduledDate, setScheduledDate] = useState({ date: '', errMsg: '' }) as any
  const [completedDate, SetCompletedDate] = useState({ date: '', errMsg: '' }) as any
  const [dateError, setDateError] = useState('')
  const [insuranceStatus, setInsuranceStatus] = useState('') as any
  const [source, setSource] = useState('') as any
  const [measurement, setMeasurement] = useState([] as any)
  const [newRecordList, setNewRecordList] = useState([] as any)
  const [showOptionalMeasurements, setShowOptionalMeasurements] = useState(false)

  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const uiResource = useSelector((state: RootState) => state.metaData.metadata)
  const patientService = new PatientService()
  const { updatePatientData } = useUpdateDataAction(patientDetail.id)

  useEffect(() => {
    getTestLists()
  }, [])


  const getTestLists = () => {
    setIsLoading(true)
    patientService.getTestsData().then((res: any) => {
      setTestsList(res?.data || [])
      setIsLoading(false)
    }).catch((error: any) => {
      setIsLoading(false)
      toast.error(Helper.getError(error), Helper.bottom_center())
    })
  }

  const handleSyncSiteToElation = async () => {
    if (!selectedSite?.id || syncingSite) return
    setSyncingSite(true)
    try {
      const response = await patientService.syncSiteToElation(selectedSite.id, patientDetail?.id)
      const data = response.data
      if (data.success) {
        const message = data.added_to_team
          ? data.message
          : `${data.message} (not added to patient's care team)`
        toast.success(message, Helper.bottom_center())
      } else {
        toast.warning(data.message || 'Site sync completed with warnings', Helper.bottom_center())
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.error?.message || 'Failed to sync site to Elation'
      toast.error(errorMsg, Helper.bottom_center())
    } finally {
      setSyncingSite(false)
    }
  }

  const getMeasurementByTest = (name: any) => {
    const prgName = (patientDetail?.program_enrollments
      ?.filter((m: any) => m.selected && m.status === 'active' && m.program_type === "evaluation")
      .map((e: any) => e.program)
      .join(',')) || ''

    name && patientService.getMeasurementsByTest(name, prgName, input?.enrollment_id).then((response: any) => {
      // Sort: needed measurements first (priority='needed' tagged by backend when
      // patient_id + enrollment_id are provided), then optional, alpha within each.
      let measurementList = response?.data?.sort((a: any, b: any) => {
        const aPriority = a?.priority === 'needed' ? 0 : 1
        const bPriority = b?.priority === 'needed' ? 0 : 1
        if (aPriority !== bPriority) return aPriority - bPriority
        return (a?.display_name || a?.name) > (b?.display_name || b?.name) ? 1 : -1
      })
      measurementList = measurementList?.map((i: any) => { return { ...i, value: '' } })
      setMeasurement(measurementList)
      let currMeasurement = measurementList?.map((i: any) => {
        return { ...i, tags: (uiResource?.MEASUREMENT_TAGS)?.[i?.name] || [] }
      })
      setNewRecordList(currMeasurement)
    }).catch((error) => toast.error(Helper.getErrorText(error), Helper.bottom_center()))
  }

  const handleSubmit = () => {
    setIsLoading(true)

    if (['order_preparation', 'ordered', 'order_notified', 'order_scheduled', 'order_follow_up', 'deferred'].includes(resultData.status)) {
      let payload = {
        name: resultData.test_name,
        status: resultData.status,
        date_recorded: (input.type === 'recommended') ? moment().unix() : moment(dateRecorded, 'YYYY-MM-DD').unix(),
        source: 'manual',
      } as any
      if (input.enrollment_id) {
        payload.enrollment_id = input.enrollment_id
      }

      if (note !== '')
        payload = { ...payload, test_notes: note }

      if (selectedSite?.id) payload = { ...payload, site_id: Number(selectedSite.id) }

      if (['order_preparation', 'ordered', 'order_follow_up'].includes(resultData.status)) {
        if (scheduledDate?.date) payload = { ...payload, scheduled_date: moment(scheduledDate.date, 'YYYY-MM-DD').unix() }

        if (completedDate?.date) payload = { ...payload, order_completed_date: moment(completedDate.date, 'YYYY-MM-DD').unix() }

        if (insuranceStatus !== '') payload = { ...payload, insurance_status: insuranceStatus }
      }

      patientService.postDiagnosticTests(patientDetail?.id, payload).then((res: any) => {
        if (res?.data?.diagnostic_test)
          updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
        setIsLoading(false)
        handleDialogClose()
      }).catch((error: any) => {
        setIsLoading(false)
        toast.error(Helper.getError(error), Helper.bottom_center())
      })
    } else if (['results_available', 'results_updated'].includes(resultData.status)) {
      const utxtimes = moment(dateRecorded).format('YYYY-MM-DD HH:mm:ss')
      const date_recorded = moment(utxtimes).format('X')

      const meaList = newRecordList
        ?.map((item: any) => ({
          kbm_id: item?.id,
          name: item?.name,
          source_type: item?.source_type,
          unit: item?.unit || item?.default_unit,
          value: item?.value || '',
          tag: (item?.tag && Object.keys(item.tag).length > 0) ? item.tag : null,
          date_recorded: date_recorded,
        }))
        ?.filter((item: any) => item.value !== '') || []

      let basePayload = {
        name: resultData.test_name,
        status: resultData.status,
        test_date_recorded: date_recorded,
        result_source: source,
      } as any

      if (note !== '')
        basePayload = { ...basePayload, test_notes: note }

      const formData = new FormData()
      Object.keys(basePayload).map((key: any) => {
        formData.append(key, basePayload[key] || '')
      })

      if (selectedFile) {
        formData.append('file', selectedFile)
        formData.append('date', date_recorded)
      }

      if (meaList.length > 0) {
        formData.append('measurements', JSON.stringify(meaList))
      }

      setIsLoading(true)
      patientDetail.id && patientService.putDiagnosticTestResult(patientDetail.id, formData).then((res: any) => {
        if (res?.data)
          updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
        setIsLoading(false)
        handleDialogClose()
      }).catch((error: any) => {
        setIsLoading(false)
        handleDialogClose()
        toast.error(Helper.getError(error), Helper.bottom_center())
      })
    }
  }

  const isDisabled = () => {
    return resultData.test_name === '' || isLoading ||
      (scheduledDate.date && scheduledDate.errMsg !== '') ||
      (!dateRecorded || dateError !== '') ||
      (['ordered', 'order_follow_up'].includes(resultData.status) && !selectedSite)
  }

  const validateDate = (d: any, name: any) => {
    const currDate = d ? moment(d).format('YYYY-MM-DD') : ''

    let errorMessage = ''
    errorMessage = (currDate === 'Invalid date')
      ? 'Please select valid date'
      : ''

    let date = currDate
    let errMsg = ''
    if (currDate === '')
      date = ''
    else if (errorMessage)
      errMsg = errorMessage

    if (name === 'scheduledDate')
      setScheduledDate({ date: date, errMsg: errMsg })
    else if (name === 'completedDate')
      SetCompletedDate({ date: date, errMsg: errMsg })
    else {
      setDateRecorded(date)
      setDateError(errMsg)
    }
  }

  const handleFileChange = (event: any) => setSelectedFile(event.target.files[0])

  const handleAddsite = () => {
    setIsAddSite(true)
  }

  const handleTestStatusChange = (value: string) => {
    setResultData({ ...resultData, status: value })
    if (value === 'results_available' && resultData.test_name !== '') {
      getMeasurementByTest(resultData.test_name)
    }
  }

  const handleTestNameChange = (selectedOption: any) => {
    if (selectedOption) {
      setResultData({ ...resultData, test_name: selectedOption.key })
      if (resultData.status === 'results_available') {
        getMeasurementByTest(selectedOption.key)
      }
    }
  }

  const getRangeUnit = (value_range: any, unit: any) => {
    if (value_range?.min === null && value_range?.max === null)
      return unit ? <Typography component={'span'} color="textSecondary" variant="subtitle2">
        &nbsp;<small>({unit})</small>
      </Typography>
        : ''

    let vrmin = ''
    let vrmax = ''
    if (value_range?.min !== null) vrmin = value_range?.min
    if (value_range?.max !== null) vrmax = value_range?.max
    return <Typography component={'span'} color="textSecondary" variant="subtitle2">
      &nbsp;<small>({vrmin}-{vrmax}{unit ? ` ${unit}` : ''})</small>
    </Typography>
  }

  const updateNewRecordVal = (id: any, value: any) => {
    setNewRecordList((prev: any) =>
      prev.map((item: any) => item.id === id ? { ...item, value: value.toString(), date_recorded: dateRecorded } : item)
    )
  }

  const deleteMeasurement = (id: any) => setNewRecordList(newRecordList.filter((i: any) => id !== i.id))

  const renderMeasurementRow = (item: any, key: any) => (
    <Grid container spacing={0} alignItems={'center'} justifyItems={'center'} key={key} direction="row" className={classes.cardLabInfo}>
      <Grid item xs={8} className={`${classes.cardLabList} ${classes.breakWord}`}>
        <Typography className={classes.desc}>
          {formatDisplayName(item?.display_name) || item?.name}
          {getRangeUnit(item?.value_range, (item?.unit || item?.default_unit))}
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <Box className={classes.measurementInputOuter}>
          {item?.possible_dropdown_values?.length > 0
            ? <Select
              displayEmpty
              variant="standard"
              label='Status'
              className={classes.selectEmpty}
              inputProps={{ 'aria-label': 'With label' }}
              name="status"
              sx={{ width: '-webkit-fill-available', padding: '0px 0px 0px 5px', marginLeft: '0rem', fontSize: '0.7rem', }}
              onChange={(e) => updateNewRecordVal(item.id, e.target.value)}
            >
              {(item?.possible_dropdown_values)?.map((v: any, indx: any) => (
                <MenuItem key={`status-${indx}`}
                  className={classes.formControlItemsPadding}
                  value={typeof v === 'boolean' ? JSON.stringify(v) : v}
                >
                  <Typography className={classes.desc} sx={{ padding: '0px !important' }}>
                    {(typeof v === 'boolean' ? JSON.stringify(v) : v)}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
            : <TextField
              id={item?.name}
              variant="standard"
              type="text"
              sx={{ height: '26px' }}
              inputProps={{ sx: { textAlign: 'right', paddingRight: '0.3rem !important' } }}
              onChange={(e) => updateNewRecordVal(item.id, e.target.value)}
            />
          }
        </Box>
      </Grid>
      <Grid item xs sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <DeleteOutlinedIcon
          sx={{ fill: "var(--color-primary)" }}
          className={classes.addCircleButton}
          onClick={() => deleteMeasurement(item.id)}
        />
      </Grid>
    </Grid>
  )

  const handleMeasurement = () => {
    if (!measurement?.length) return <></>
    // Backend tags items with priority='needed' when patient_id+enrollment_id
    // are passed; if neither tag is present (legacy/no-priority case), all
    // items render as a single flat list.
    const hasPriority = measurement.some((m: any) => m?.priority === 'needed' || m?.priority === 'optional')
    const needed = hasPriority ? measurement.filter((m: any) => m?.priority === 'needed') : measurement
    const optional = hasPriority ? measurement.filter((m: any) => m?.priority !== 'needed') : []
    return (
      <Stack spacing={0.5} style={{ maxHeight: '25rem', width: '70%' }}>
        <Typography pl={0.2} variant='subtitle2' className={classes.taskInput}>Measurements</Typography>
        {needed.map((item: any, key: any) => renderMeasurementRow(item, `n-${key}`))}
        {optional.length > 0 && (
          <>
            <Button
              size="small"
              onClick={() => setShowOptionalMeasurements(!showOptionalMeasurements)}
              sx={{ alignSelf: 'flex-start', textTransform: 'none', fontSize: '0.75rem', padding: '2px 8px' }}
            >
              {showOptionalMeasurements ? `Hide other ${optional.length} measurements` : `Show all (+${optional.length})`}
            </Button>
            {showOptionalMeasurements && optional.map((item: any, key: any) => renderMeasurementRow(item, `o-${key}`))}
          </>
        )}
      </Stack>
    )
  }

  const statusOptions = Helper.isAIDA()
    ? [
      { key: 'ordered', label: 'Ordered', title: 'Order' },
      { key: 'order_notified', label: 'Order Notified', title: 'Order Notified' },
      { key: 'order_scheduled', label: 'Order Scheduled', title: 'Order Scheduled' },
      { key: 'results_available', label: 'Results available', title: 'Result' },
      { key: 'results_updated', label: 'Results updated', title: 'Results updated' },
      { key: 'deferred', label: 'Deferred', title: 'Deferred' },
    ] as any
    : [
      { key: 'order_preparation', label: 'Order preparation', title: 'Order preparation' },
      { key: 'ordered', label: 'Ordered', title: 'Order' },
      { key: 'order_notified', label: 'Order Notified', title: 'Order Notified' },
      { key: 'order_scheduled', label: 'Order Scheduled', title: 'Order Scheduled' },
      { key: 'results_available', label: 'Results available', title: 'Result' },
      { key: 'results_updated', label: 'Results updated', title: 'Results updated' },
      { key: 'deferred', label: 'Deferred', title: 'Deferred' },
    ] as any

  const renderAddTest = () => {
    return (
      <Stack width={'52%'} alignItems={'baseline'} justifyItems={'center'} spacing={1.2} direction="column">
        <Stack direction={'row'} spacing={4}>
          <Box>
            <Typography pl={0.2} variant="subtitle2" className={classes?.taskInput}>
              Name
            </Typography>
            <Autocomplete
              id="add-name"
              options={testsList}
              getOptionLabel={(option: any) => formatDisplayName(option.display_name) || option.display_name}
              filterOptions={createFilterOptions({ matchFrom: 'any', stringify: (option: any) => formatDisplayName(option?.display_name) || option?.display_name })}
              onChange={(e, selectedOption) => handleTestNameChange(selectedOption)}
              disableClearable
              ListboxProps={{ sx: { '& .MuiAutocomplete-option': { padding: '2px 0px 2px 8px !important' } } }}
              sx={{ width: '9.5rem' }}
              renderInput={(params: any) => <TextField {...params} variant="standard" size="small" InputProps={{ ...params.InputProps }} />}
            />
          </Box>
          <Box pb={0.5}>
            <Typography pl={0.2} variant="subtitle2" className={classes?.taskInput}>
              Status
            </Typography>
            <FormControl className={classes?.formControlStage}>
              <Select
                id="test-status"
                labelId="test-status-label"
                variant="standard"
                value={resultData.status}
                onChange={(e) => handleTestStatusChange(e?.target?.value || 'order_preparation')}
                sx={{ width: '9rem !important' }}
                inputProps={{ classes: { icon: classes?.icon } }}
              >
                {statusOptions.map((option: any) => (
                  <MenuItem key={option.key} value={option.key} className={classes?.programMenu}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Stack>

        <Box>
          <TextField
            id="outlined-basic"
            name="test_notes"
            variant="outlined"
            placeholder="Add note here"
            size="small"
            value={note}
            inputProps={{ style: { fontSize: '0.7rem' } }}
            InputLabelProps={{ style: { fontSize: '0.7rem' } }}
            multiline={true}
            onBlur={() => setIsNoteFocused(false)}
            onFocus={() => setIsNoteFocused(true)}
            rows="2"
            onChange={(e: any) => setNote(e.target.value)}
            sx={{
              width: '20rem !important',
              '& .MuiOutlinedInput-root': { padding: '0.2rem 0rem 0.2rem 0.5rem !important' },
            }}
          />
        </Box>

        {/* Site Selection - available for all test statuses */}
        {isControlVisible('test_select_site') && !selectedSite && (
          <Box pt={0.3}>
            <Button variant="outlined" size="small" sx={{ padding: '0.2rem 0.6rem !important' }} onClick={handleAddsite} startIcon={<LibraryAddIcon className={classes.editIcon} />}>
              Select site
            </Button>
          </Box>
        )}
        {/* Display selected site details */}
        {selectedSite && (
          <Box
            sx={{
              backgroundColor: 'var(--color-background-subtle)',
              borderRadius: 1,
              p: 1,
              mt: 0.5,
              border: '1px solid var(--color-border-light)',
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                  {selectedSite.name}
                </Typography>
                {(selectedSite.address || selectedSite.city || selectedSite.state) && (
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                    {[selectedSite.address, selectedSite.city, selectedSite.state, selectedSite.zip].filter(Boolean).join(', ')}
                  </Typography>
                )}
                {selectedSite.phone && (
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                    Phone: {selectedSite.phone}
                  </Typography>
                )}
                {selectedSite.fax && (
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                    Fax: {selectedSite.fax}
                  </Typography>
                )}
              </Box>
              <Stack direction="row" spacing={0.5} alignItems="center">
                {Helper.checkSouceDeatils(patientDetail?.source_details) && (
                  <Tooltip title="Sync to Elation">
                    <IconButton size="small" onClick={handleSyncSiteToElation} disabled={syncingSite} sx={{ p: 0.25 }}>
                      {syncingSite ? <CircularProgress size={14} /> : <CloudUploadIcon fontSize="small" color="primary" />}
                    </IconButton>
                  </Tooltip>
                )}
                <Button
                  size="small"
                  variant="text"
                  onClick={handleAddsite}
                  sx={{
                    minWidth: 'auto',
                    p: 0.5,
                    fontSize: '0.65rem',
                    color: 'text.secondary',
                    textTransform: 'none',
                  }}
                >
                  Change
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setSelectedSite(null)}
                  sx={{
                    minWidth: 'auto',
                    p: 0.5,
                    fontSize: '0.65rem',
                    color: 'var(--color-primary)',
                    textTransform: 'none',
                  }}
                >
                  Clear
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}

        {['order_preparation', 'ordered', 'order_follow_up'].includes(resultData.status) && (
          <>
            <Stack direction="row" spacing={4} pt={0.3} justifyContent={'flex-start'}>
              {['order_follow_up'].includes(resultData.status) && (
                <Box>
                  <Typography pl={0.2} pt={0.2} variant="subtitle2" className={classes?.taskInput}>
                    {['ordered', 'order_follow_up'].includes(resultData.status) ? 'Ordered date' : 'Date'}
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      className={classes?.taskSchedule}
                      name={'date'}
                      format={'MM/DD/YYYY'}
                      formatDensity="dense"
                      slotProps={{
                        textField: { size: 'small', error: false },
                        actionBar: { actions: ['clear'] },
                      }}
                      disablePast={false}
                      value={moment(moment(dateRecorded, 'YYYY-MM-DD'), 'MM/DD/YYYY')}
                      onChange={(e: any) => validateDate(e, 'dateRecorded')}
                    />
                  </LocalizationProvider>
                  {dateError && (
                    <Typography color={'red'} className={classes?.desc}>
                      {dateError}
                    </Typography>
                  )}
                </Box>
              )}
              <Box>
                <Typography pl={0.2} pt={0.2} variant="subtitle2" className={classes?.taskInput}>
                  Ordered date
                </Typography>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    className={classes?.taskSchedule}
                    name={'date'}
                    format={'MM/DD/YYYY'}
                    formatDensity="dense"
                    slotProps={{
                      textField: { size: 'small', error: false },
                      actionBar: { actions: ['clear'] },
                    }}
                    value={moment(moment(completedDate.date, 'YYYY-MM-DD'), 'MM/DD/YYYY')}
                    onChange={(e: any) => validateDate(e, 'completedDate')}
                  />
                </LocalizationProvider>
                {completedDate.errMsg && (
                  <Typography color={'red'} className={classes?.desc}>
                    {completedDate.errMsg}
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography pb={0.2} variant="subtitle2" className={classes?.taskInput}>
                  Scheduled date
                </Typography>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    className={classes?.taskSchedule}
                    name={'date'}
                    format={'MM/DD/YYYY'}
                    formatDensity="dense"
                    slotProps={{
                      textField: { size: 'small', error: false },
                      actionBar: { actions: ['clear'] },
                    }}
                    value={moment(moment(scheduledDate.date, 'YYYY-MM-DD'), 'MM/DD/YYYY')}
                    onChange={(e: any) => validateDate(e, 'scheduledDate')}
                  />
                </LocalizationProvider>
                {scheduledDate.errMsg && (
                  <Typography color={'red'} className={classes?.desc}>
                    {scheduledDate.errMsg}
                  </Typography>
                )}
              </Box>
            </Stack>

            <Box pt={1.2}>
              <Typography pl={0.2} variant="subtitle2" className={classes.taskInput}>
                Insurance status
              </Typography>
              <FormControl className={classes.formControlStage}>
                <Select
                  id="insurance-status"
                  labelId="insurance-status-label"
                  variant="standard"
                  value={insuranceStatus}
                  onChange={(e) => setInsuranceStatus(e?.target?.value || '')}
                  sx={{ width: '8rem !important' }}
                  inputProps={{ classes: { icon: classes.icon } }}
                >
                  <MenuItem key={''} value={''} className={classes.programMenu}>
                    {''}
                  </MenuItem>
                  <MenuItem key={'Approved'} value={'Approved'} className={classes.programMenu}>
                    {'Approved'}
                  </MenuItem>
                  <MenuItem key={'Rejected'} value={'Rejected'} className={classes.programMenu}>
                    {'Rejected'}
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          </>
        )}

        {resultData.status === 'results_available' && (
          <>
            <Box>
              <Typography pl={0.2} pt={0.2} variant="subtitle2" className={classes?.taskInput}>
                Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  className={classes?.taskSchedule}
                  name={'date'}
                  format={'MM/DD/YYYY'}
                  formatDensity="dense"
                  slotProps={{
                    textField: { size: 'small', error: false },
                    actionBar: { actions: ['clear'] },
                  }}
                  value={moment(moment(dateRecorded, 'YYYY-MM-DD'), 'MM/DD/YYYY')}
                  onChange={(e: any) => validateDate(e, 'dateRecorded')}
                />
              </LocalizationProvider>
              {dateError && (
                <Typography color={'red'} className={classes?.desc}>
                  {dateError}
                </Typography>
              )}
            </Box>

            <Box pt={0.5}>
              <Typography pl={0.2} variant="subtitle2" className={classes?.taskInput}>
                Result source
              </Typography>
              <FormControl className={classes?.formControlStage}>
                <Select
                  id="source"
                  labelId="source"
                  variant="standard"
                  value={source}
                  onChange={(e) => setSource(e?.target?.value || '')}
                  sx={{ width: '5rem !important' }}
                  inputProps={{ classes: { icon: classes?.icon } }}
                >
                  <MenuItem key={''} value={''} className={classes?.programMenu}>{`  `}</MenuItem>
                  <MenuItem key={'external'} value={'external'} className={classes?.programMenu}>
                    {'External'}
                  </MenuItem>
                  <MenuItem key={'internal'} value={'internal'} className={classes?.programMenu}>
                    {'Internal'}
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Stack direction={'column'} pt={0.5} className={classes?.fullWidth}>
              <Box pb={1}>
                <FormControl>
                  <Input
                    inputProps={{ accept: '.pdf' }}
                    sx={{ position: 'absolute', display: 'none', padding: '0rem !important' }}
                    id="upload-file"
                    type="file"
                    name="inputFile"
                    onChange={handleFileChange}
                  />
                  <label htmlFor="upload-file">
                    <Button component="span" variant="outlined" size="small" sx={{ padding: '0.2rem !important', width: '5rem !important' }}>
                      Choose file
                    </Button>
                  </label>
                </FormControl>
                {selectedFile && (
                  <Typography variant="caption" pl={1.5}>
                    {' '}
                    {selectedFile?.name}
                  </Typography>
                )}
              </Box>
              {handleMeasurement()}
            </Stack>
          </>
        )}
      </Stack>
    )
  }

  return (
    <Draggable nodeRef={monitorRef} defaultPosition={{ x: 0, y: 0 }} handle=".add-test-dialog">
      <Dialog
        ref={monitorRef}
        id={'add-test'}
        fullWidth={true}
        maxWidth="md"
        open={open}
        onClose={handleDialogClose}
        aria-labelledby="Add test"
        hideBackdrop
        disableScrollLock
        onKeyUp={(e) => {
          e.preventDefault()
          if (e.key === 'Enter' && !isDisabled() && !isNoteFocused) handleSubmit()
        }}
        sx={{ position: 'absolute', left: '15%', top: '20%' }}
      >
        <DialogTitle className={classes?.addPatientQuestionHeaderSection} classes={{ root: 'add-test-dialog' }}>
          <DialogTitleHeaderComponent title={`Add test`} handleDialogClose={handleDialogClose} />
        </DialogTitle>
        <Box> {isLoading && <LinearProgress />} </Box>
        <DialogContent className={classes?.addPatientDialogContent} sx={{ padding: '0.8rem 0.5rem 1rem 1.8rem !important', minHeight: '18rem !important' }}>
          {renderAddTest()}
        </DialogContent>
        <Grid container spacing={2} className={classes?.containerPadding} justifyContent="flex-end">
          <Grid item>
            <Button variant="outlined" size="small" disabled={isDisabled()} onClick={handleSubmit} component="label" className={isDisabled() ? classes.disabledBtn : ''}>
              Submit
            </Button>
          </Grid>
          <Grid item>
            <Button variant="outlined" size="small" onClick={handleDialogClose}>
              Cancel
            </Button>
          </Grid>
        </Grid>
        {isAddSite && (
          <SiteListComponent
            open={isAddSite}
            item={{
              title: 'Select Site',
              isAddSite: true,
              isSelectMode: true,
            }}
            handleDialogClose={() => setIsAddSite(false)}
            onSelectSite={async (site: any) => {
              // Fetch linked phone numbers for the site
              try {
                const phonesRes = await patientService.getSitePhones(site.id)
                const phones = phonesRes.data?.phones || []
                const phoneNumber = phones.find((p: any) => p.number_type === 'phone')?.number
                const faxNumber = phones.find((p: any) => p.number_type === 'fax')?.number
                setSelectedSite({
                  ...site,
                  phone: phoneNumber || site.phone,
                  fax: faxNumber || site.fax,
                })
              } catch (err) {
                // Fall back to site's own phone/fax if fetch fails
                setSelectedSite(site)
              }
              setIsAddSite(false)
            }}
          />
        )}
      </Dialog>
    </Draggable>
  )
};

export default withStyles(stylesheet)(AddTestDialogComponent)